import { useEffect, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { Button } from "@typedoc/ui";
import { useInsideMain } from "./main-landmark";
import { SwapText } from "./swap-text";
import { WaitActions, WaitDetail, WaitMark, useSlow } from "./wait-screen";

/**
 * The waiting screen that last left, when it left, and when its wait began. Each route level that
 * waits mounts its own screen ("Loading…", then "Getting your workspace ready…"), so without this
 * every handover faded the whole screen out and in again, swapped the words in one cut, and
 * started the count towards "longer than usual" over.
 */
let departed: { label: string; at: number; since: number } | null = null;

/** Close enough to read as one wait: the next level's screen mounts in the same tick or so. */
const HANDOVER_MS = 200;

/**
 * What any screen shows while it cannot yet say anything true: the session is still being read,
 * or where this person belongs is not settled. It holds the whole screen rather than a layout,
 * because showing the layout would be showing a screen the answer may take away.
 *
 * The router only mounts it once a wait is long enough to notice, and then keeps it long enough
 * to read (`main.tsx`), so a quick answer never flashes it. When it stands in for a whole route
 * it is the page's main landmark itself, so the root's skip link always has a target; inside a
 * layout that already has one, it is only the status.
 *
 * One that takes over from another carries on instead of starting again: no fade in, the mark
 * keeps its rhythm, and the words change in place. A wait that runs past ten seconds says so and
 * offers to ask again, rather than showing the same words for as long as it takes.
 */
export function PendingScreen({ label = "Loading…" }: { label?: string | undefined }) {
  const insideMain = useInsideMain();
  const router = useRouter();
  const [handed] = useState(() =>
    departed && performance.now() - departed.at < HANDOVER_MS ? departed : null,
  );
  const [since] = useState(() => handed?.since ?? performance.now());
  const slow = useSlow(since);
  // Recorded as it goes (and on a change of words, which is harmless: only a screen mounting
  // within the handover window reads it).
  useEffect(
    () => () => {
      departed = { label, at: performance.now(), since };
    },
    [label, since],
  );
  const status = (
    <output className="pending-screen" data-continued={handed === null ? undefined : ""}>
      <WaitMark />
      <span className="wait-title">
        <SwapText text={label} from={handed?.label ?? null} />
      </span>
      <WaitDetail slow={slow} />
      <WaitActions slow={slow}>
        <Button variant="outline" size="sm" onClick={() => void router.invalidate()}>
          Try again
        </Button>
      </WaitActions>
    </output>
  );
  return insideMain ? status : <main id="main">{status}</main>;
}
